import React, { FC, useState, useCallback, useMemo } from 'react'
import { UseFormMethods } from 'react-hook-form'
import pick from 'lodash/pick'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import { Trailer, ITrailerProps } from 'apps/fleet-planner/pages/loads/Trailer'
import { ILoad } from 'apps/fleet-planner/types'

interface IControlledTrailerProps {
  name?: string
  defaultValue?: Partial<
    Pick<ILoad, 'trailerId' | 'trailerLength' | 'trailerType' | 'trailer' | 'trailerNumber'>
  >
  TrailerProps?: Partial<ITrailerProps>
  control?: UseFormMethods['control']
  setValue: UseFormMethods['setValue']

  // Sets value using trailerNumber instead of id
  useNumber?: boolean
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledTrailer
 *   control={control}
 *   TrailerProps={{
 *     selectedMotorCarrierId: 90210
 *   }} />
 * ```
 */
export const ControlledTrailer: FC<IControlledTrailerProps> = ({
  name = 'trailerId',
  defaultValue = {},
  TrailerProps = {},
  control,
  setValue,
  useNumber = false,
}) => {
  const [load, setLoad] = useState<Partial<ILoad>>({
    ...defaultValue,
  })

  const handleChange = (change: Partial<ILoad> | undefined) => {
    const { trailer } = change || {}
    setValue(name, useNumber ? trailer?.trailerNumber : trailer?.id)
    setValue('trailerLength', trailer?.trailerLength)
    setValue('trailerType', trailer?.trailerType)
    setLoad(prev => ({
      ...prev,
      ...change,
    }))
  }

  const { error = '', large = true, refetchOptions = false, selectedMotorCarrierId } =
    TrailerProps || {}

  /**
   * Formats the value using `trailerId` as a backup value
   */
  /**
   * Formats the value using `truckId` as a backup value
   */
  const getValue = useCallback((): Pick<ILoad, 'trailer'> => {
    return pick(load, 'trailer')
  }, [load])

  const value = useMemo(() => getValue(), [getValue])

  const {
    trailerId: defaultTrailerId,
    trailerLength: defaultTrailerLength,
    trailerType: defaultTrailerType,
  } = defaultValue || {}

  return (
    <>
      <Trailer
        large={large}
        value={value}
        onChange={handleChange}
        refetchOptions={refetchOptions}
        error={error}
        selectedMotorCarrierId={selectedMotorCarrierId}
      />
      {/**
       * Register inputs for use with RHF
       */}
      <ControlledHiddenInput name={name} defaultValue={defaultTrailerId} control={control} />
      <ControlledHiddenInput
        name='trailerLength'
        defaultValue={defaultTrailerLength}
        control={control}
      />
      <ControlledHiddenInput
        name='trailerType'
        defaultValue={defaultTrailerType}
        control={control}
      />
    </>
  )
}
